// UserForm.js
import React, { useState } from "react";
import {
  EuiForm,
  EuiFormRow,
  EuiFieldText,
  EuiFieldPassword,
  EuiSelect,
  EuiFieldNumber,
  EuiTextArea,
  EuiButton,
  EuiText,
  EuiCheckboxGroup,
  EuiSwitch,
} from "@elastic/eui";
import useFetch from "react-fetch-hook";
import { PostOptions } from "../utils/RequestUtils";
import { getApiUrl } from "../utils/api";

const PRESENTATION_STYLE_OPTIONS = [
  { id: "narrative", label: "Narrative" },
  { id: "bullet_points", label: "Bullet Points" },
  { id: "diagrams", label: "Diagrams" },
  { id: "real_world_examples", label: "Real-World Examples" },
];

const CourseAddForm = ({ onSuccess }) => {
  const [form, setForm] = useState({
    course_name: "",
    course_number: "",
    course_description: "",
    image_url: "https://picsum.photos/400/200",
    mode: "step-by-step",
    default_assistance_level: 1,
    subject_domain: "",
    grade_level: "",
    presentation_styles: [],
    student_style_choice_enabled: false,
  });

  const [isLoading, setisLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({
      ...form,
      [name]: value,
    });
  };

  const handleStyleCheckboxChange = (optionId) => {
    setForm((prev) => ({
      ...prev,
      presentation_styles: prev.presentation_styles.includes(optionId)
        ? prev.presentation_styles.filter((s) => s !== optionId)
        : [...prev.presentation_styles, optionId],
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setErrorMessage(null);
    setHasError(false);
    setisLoading(true);
    fetch(getApiUrl("/api/course/add"), PostOptions(form))
      .then((response) => {
        return response.json().then((text) => {
          if (!response.ok) {
            setErrorMessage(text.message);
            setHasError(true);
            alert(text.message);
            throw new Error(text.message);
          }
          return text;
        });
      })
      .then((data) => {
        setisLoading(false);
        onSuccess();
      })
      .catch((error) => {
        console.error("There was an error!", error);
        setisLoading(false);
      });
  };

  return (
    <EuiForm
      component="form"
      onSubmit={handleSubmit}
      isInvalid={hasError}
      error={errorMessage}
    >
      <EuiFormRow label="CourseName">
        <EuiFieldText
          name="course_name"
          value={form.course_name}
          onChange={handleChange}
        />
      </EuiFormRow>

      <EuiFormRow label="Course Number">
        <EuiFieldText
          name="course_number"
          value={form.course_number}
          onChange={handleChange}
        />
      </EuiFormRow>

      <EuiFormRow label="Course Description">
        <EuiTextArea
          fullWidth
          value={form.course_description}
          onChange={handleChange}
          name="course_description"
        />
      </EuiFormRow>

      <EuiFormRow label="Subject domain" helpText="e.g. Mathematics, Computer Science. Feeds into AI for age-appropriate vocabulary.">
        <EuiFieldText
          name="subject_domain"
          value={form.subject_domain}
          onChange={handleChange}
          placeholder="e.g. Mathematics"
        />
      </EuiFormRow>

      <EuiFormRow label="Grade level" helpText="e.g. High school, Grade 9, College. Feeds into AI for complexity.">
        <EuiFieldText
          name="grade_level"
          value={form.grade_level}
          onChange={handleChange}
          placeholder="e.g. High school (9-12)"
        />
      </EuiFormRow>

      <EuiFormRow label="Image URL">
        <EuiFieldText
          name="image_url"
          value={form.image_url}
          onChange={handleChange}
        />
      </EuiFormRow>

      <EuiFormRow label="Mode">
        <EuiSelect
          name="mode"
          options={[
            { value: "step-by-step", text: "Step-by-step" },
            { value: "concise", text: "Concise" },
            { value: "both", text: "Both" },
          ]}
          value={form.mode}
          onChange={handleChange}
        />
      </EuiFormRow>

      <EuiFormRow label="Default AI Assistance Level">
        <EuiSelect
          name="default_assistance_level"
          options={[
            { value: 1, text: "Level 1 - Hint Only (concepts and hints)" },
            { value: 2, text: "Level 2 - Review (feedback only)" },
            { value: 3, text: "Level 3 - Troubleshoot (find and fix errors)" },
            { value: 4, text: "Level 4 - Co-Builder (work through together)" },
            { value: 5, text: "Level 5 - Full Solutions (learning only)" },
          ]}
          value={form.default_assistance_level}
          onChange={(e) => setForm({ ...form, default_assistance_level: parseInt(e.target.value, 10) })}
        />
      </EuiFormRow>

      <EuiFormRow
        label="Presentation Styles"
        helpText="Select how content is delivered. Multiple selections are combined."
      >
        <EuiCheckboxGroup
          options={PRESENTATION_STYLE_OPTIONS}
          idToSelectedMap={Object.fromEntries(
            PRESENTATION_STYLE_OPTIONS.map((opt) => [
              opt.id,
              form.presentation_styles.includes(opt.id),
            ])
          )}
          onChange={handleStyleCheckboxChange}
        />
      </EuiFormRow>

      <EuiFormRow helpText="When enabled, students can pick their own presentation style in the chat.">
        <EuiSwitch
          label="Let students choose their own style"
          checked={form.student_style_choice_enabled}
          onChange={(e) =>
            setForm({ ...form, student_style_choice_enabled: e.target.checked })
          }
        />
      </EuiFormRow>

      <EuiButton type="submit" fill isLoading={isLoading}>
        Create
      </EuiButton>
    </EuiForm>
  );
};

export default CourseAddForm;
